import React, { useState, useEffect, useMemo } from 'react';
import {
  Building2, TrendingUp, TrendingDown, DollarSign, BarChart3, Calendar, Download,
  RefreshCw, PieChart as PieChartIcon, ArrowUpRight, ArrowDownRight, Minus,
  FileSpreadsheet, Printer, ArrowLeftRight, AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import apiService from '../services/apiService';

// ============================================================
// CONFIG: Shared company ID - adjust if your IDs are different
// ============================================================
const SHARED_COMPANY_ID = 3;    // Vatu-Amazing (shared)
const COMPANY_1_ID = 1;         // Amazing Pure
const COMPANY_2_ID = 2;         // Vatu

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ProfitLossReport = () => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedView, setSelectedView] = useState('single'); // single, comparison
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');

  // Date range
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [periodType, setPeriodType] = useState('monthly');

  // Raw data
  const [allInvoices, setAllInvoices] = useState({});   // keyed by companyId
  const [allExpenses, setAllExpenses] = useState({});    // keyed by companyId
  const [allProducts, setAllProducts] = useState({});    // keyed by companyId - for cost lookup
  const [sharedExpenses, setSharedExpenses] = useState([]);

  // Processed P&L data
  const [plData, setPlData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [expenseBreakdown, setExpenseBreakdown] = useState([]);
  const [sharedExpenseDetail, setSharedExpenseDetail] = useState([]);

  // UI state
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    statement: true,
    trends: true,
    expenses: true,
    shared: true,
    comparison: true
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId || selectedView === 'comparison') {
      loadAllData();
    }
  }, [selectedCompanyId, selectedView, dateRange]);

  useEffect(() => {
    if (Object.keys(allInvoices).length > 0 || Object.keys(allExpenses).length > 0) {
      processPlData();
    }
  }, [allInvoices, allExpenses, allProducts, sharedExpenses, selectedCompanyId, selectedView, periodType]);

  const loadCompanies = async () => {
    try {
      const data = await apiService.getAllCompanies();
      setCompanies(data);
      // Only show actual business companies (not shared entity)
      const businessCompanies = data.filter(c => c.id !== SHARED_COMPANY_ID);
      const savedCompanyId = localStorage.getItem('selectedCompanyId');
      if (savedCompanyId && businessCompanies.find(c => c.id === parseInt(savedCompanyId))) {
        setSelectedCompanyId(savedCompanyId);
        setSelectedCompanyName(businessCompanies.find(c => c.id === parseInt(savedCompanyId))?.name || '');
      } else if (businessCompanies.length > 0) {
        setSelectedCompanyId(businessCompanies[0].id.toString());
        setSelectedCompanyName(businessCompanies[0].name);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    localStorage.setItem('selectedCompanyId', companyId);
    const company = companies.find(c => c.id === parseInt(companyId));
    setSelectedCompanyName(company?.name || '');
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      apiService.refreshTokenFromStorage();

      const companyIds = selectedView === 'comparison'
        ? [COMPANY_1_ID, COMPANY_2_ID]
        : [parseInt(selectedCompanyId)];

      const invoiceMap = {};
      const expenseMap = {};
      const productMap = {};

      // Load invoices, expenses, and products for each company
      for (const cid of companyIds) {
        const [inv, exp, prod] = await Promise.all([
          apiService.getInvoicesByCompany(cid),
          apiService.getExpensesByCompany(cid),
          apiService.getProductsByCompany(cid)
        ]);
        invoiceMap[cid] = filterByDateRange(inv, 'invoiceDate');
        expenseMap[cid] = filterByDateRange(exp, 'expenseDate');
        productMap[cid] = prod;
      }

      // Always load shared expenses
      let shared = [];
      try {
        shared = await apiService.getExpensesByCompany(SHARED_COMPANY_ID);
        shared = filterByDateRange(shared, 'expenseDate');
      } catch (e) {
        console.warn('No shared company expenses found');
      }

      setAllInvoices(invoiceMap);
      setAllExpenses(expenseMap);
      setAllProducts(productMap);
      setSharedExpenses(shared);
    } catch (error) {
      console.error('Error loading P&L data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterByDateRange = (data, dateField) => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    end.setHours(23, 59, 59);
    return data.filter(item => {
      const d = new Date(item[dateField] || item.createdAt);
      return d >= start && d <= end;
    });
  };

  // =============================================
  // P&L COMPUTATION ENGINE
  // =============================================
  const computeCompanyPL = (companyId) => {
    const invoices = allInvoices[companyId] || [];
    const ownExpenses = allExpenses[companyId] || [];
    const activeInvoices = invoices.filter(inv => inv.status !== 'CANCELLED');

    // Revenue
    const revenue = activeInvoices.reduce((sum, inv) => sum + parseFloat(inv.totalAmount || 0), 0);
    const collected = activeInvoices.reduce((sum, inv) => sum + parseFloat(inv.paidAmount || 0), 0);
    const outstanding = activeInvoices.reduce((sum, inv) => sum + parseFloat(inv.balanceAmount || 0), 0);

    // Build product cost lookup from loaded products
    const products = allProducts[companyId] || [];
    const costMap = {};
    products.forEach(p => {
      costMap[p.id] = parseFloat(p.cost || 0);
    });

    // COGS: sum of (quantity * cost) from invoice items
    // Try item.product.cost first (from API), fallback to products list lookup
    let cogs = 0;
    activeInvoices.forEach(inv => {
      (inv.items || []).forEach(item => {
        const qty = parseFloat(item.quantity || 0);
        const productId = item.product?.id || item.productId;
        const cost = parseFloat(item.product?.cost || 0) || costMap[productId] || 0;
        cogs += qty * cost;
      });
    });

    // Gross Profit
    const grossProfit = revenue - cogs;
    const grossMargin = revenue > 0 ? (grossProfit / revenue * 100) : 0;

    // Own Expenses
    const ownExpenseTotal = ownExpenses.reduce((sum, exp) => sum + parseFloat(exp.price || 0), 0);
    const ownExpensePaid = ownExpenses.reduce((sum, exp) => sum + parseFloat(exp.paidAmount || 0), 0);

    // Shared Expenses (50% of company 3)
    const sharedTotal = sharedExpenses.reduce((sum, exp) => sum + parseFloat(exp.price || 0), 0);
    const sharedAllocation = sharedTotal / 2;
    const sharedPaid = sharedExpenses.reduce((sum, exp) => sum + parseFloat(exp.paidAmount || 0), 0) / 2;

    // Total Expenses
    const totalExpenses = ownExpenseTotal + sharedAllocation;
    const totalExpensesPaid = ownExpensePaid + sharedPaid;

    // Net Profit
    const netProfit = grossProfit - totalExpenses;
    const netMargin = revenue > 0 ? (netProfit / revenue * 100) : 0;

    // Expense breakdown by category/name
    const expenseCategories = {};
    ownExpenses.forEach(exp => {
      const cat = exp.name || 'Other';
      expenseCategories[cat] = (expenseCategories[cat] || 0) + parseFloat(exp.price || 0);
    });

    // Shared expense categories with (Shared) label
    const sharedCategories = {};
    sharedExpenses.forEach(exp => {
      const cat = (exp.name || 'Other') + ' (Shared 50%)';
      sharedCategories[cat] = (sharedCategories[cat] || 0) + (parseFloat(exp.price || 0) / 2);
    });

    return {
      companyId,
      companyName: companies.find(c => c.id === companyId)?.name || `Company ${companyId}`,
      revenue,
      collected,
      outstanding,
      cogs,
      grossProfit,
      grossMargin,
      ownExpenses: ownExpenseTotal,
      ownExpensesPaid: ownExpensePaid,
      sharedExpenses: sharedAllocation,
      sharedExpensesPaid: sharedPaid,
      totalExpenses,
      totalExpensesPaid,
      netProfit,
      netMargin,
      invoiceCount: activeInvoices.length,
      expenseCategories,
      sharedCategories
    };
  };

  const computeTrendData = (companyId) => {
    const invoices = (allInvoices[companyId] || []).filter(inv => inv.status !== 'CANCELLED');
    const ownExpenses = allExpenses[companyId] || [];
    const grouped = {};

    // Build product cost lookup
    const products = allProducts[companyId] || [];
    const costMap = {};
    products.forEach(p => { costMap[p.id] = parseFloat(p.cost || 0); });

    // Group invoices by period
    invoices.forEach(inv => {
      const key = getPeriodKey(inv.invoiceDate);
      if (!grouped[key]) {
        grouped[key] = { period: key, revenue: 0, cogs: 0, ownExpenses: 0, sharedExpenses: 0 };
      }
      grouped[key].revenue += parseFloat(inv.totalAmount || 0);
      (inv.items || []).forEach(item => {
        const productId = item.product?.id || item.productId;
        const cost = parseFloat(item.product?.cost || 0) || costMap[productId] || 0;
        grouped[key].cogs += parseFloat(item.quantity || 0) * cost;
      });
    });

    // Group own expenses by period
    ownExpenses.forEach(exp => {
      const key = getPeriodKey(exp.expenseDate || exp.createdAt);
      if (!grouped[key]) {
        grouped[key] = { period: key, revenue: 0, cogs: 0, ownExpenses: 0, sharedExpenses: 0 };
      }
      grouped[key].ownExpenses += parseFloat(exp.price || 0);
    });

    // Group shared expenses (50%)
    sharedExpenses.forEach(exp => {
      const key = getPeriodKey(exp.expenseDate || exp.createdAt);
      if (!grouped[key]) {
        grouped[key] = { period: key, revenue: 0, cogs: 0, ownExpenses: 0, sharedExpenses: 0 };
      }
      grouped[key].sharedExpenses += parseFloat(exp.price || 0) / 2;
    });

    return Object.values(grouped)
      .sort((a, b) => a.period.localeCompare(b.period))
      .map(item => ({
        ...item,
        label: formatPeriodLabel(item.period),
        grossProfit: item.revenue - item.cogs,
        totalExpenses: item.ownExpenses + item.sharedExpenses,
        netProfit: (item.revenue - item.cogs) - (item.ownExpenses + item.sharedExpenses),
        margin: item.revenue > 0 ? (((item.revenue - item.cogs) - (item.ownExpenses + item.sharedExpenses)) / item.revenue * 100) : 0
      }));
  };

  const getPeriodKey = (dateStr) => {
    const date = new Date(dateStr);
    if (periodType === 'daily') {
      return date.toISOString().split('T')[0];
    } else if (periodType === 'quarterly') {
      const q = Math.floor(date.getMonth() / 3) + 1;
      return `${date.getFullYear()}-Q${q}`;
    } else if (periodType === 'yearly') {
      return `${date.getFullYear()}`;
    } else {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }
  };

  const formatPeriodLabel = (key) => {
    if (periodType === 'daily') return key;
    if (periodType === 'quarterly') return key;
    if (periodType === 'yearly') return key;
    const [year, month] = key.split('-');
    return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
  };

  const processPlData = () => {
    if (selectedView === 'comparison') {
      const pl1 = computeCompanyPL(COMPANY_1_ID);
      const pl2 = computeCompanyPL(COMPANY_2_ID);

      // Combined view
      const combined = {
        companyName: 'Combined',
        revenue: pl1.revenue + pl2.revenue,
        collected: pl1.collected + pl2.collected,
        outstanding: pl1.outstanding + pl2.outstanding,
        cogs: pl1.cogs + pl2.cogs,
        grossProfit: pl1.grossProfit + pl2.grossProfit,
        grossMargin: (pl1.revenue + pl2.revenue) > 0
          ? ((pl1.grossProfit + pl2.grossProfit) / (pl1.revenue + pl2.revenue) * 100) : 0,
        ownExpenses: pl1.ownExpenses + pl2.ownExpenses,
        sharedExpenses: pl1.sharedExpenses, // Don't double count - shared total = what it is
        totalExpenses: pl1.totalExpenses + pl2.totalExpenses,
        netProfit: pl1.netProfit + pl2.netProfit,
        netMargin: (pl1.revenue + pl2.revenue) > 0
          ? ((pl1.netProfit + pl2.netProfit) / (pl1.revenue + pl2.revenue) * 100) : 0,
        invoiceCount: pl1.invoiceCount + pl2.invoiceCount
      };

      setComparisonData({ company1: pl1, company2: pl2, combined });
      setPlData(combined);

      // Trend: merge both company trends
      const trend1 = computeTrendData(COMPANY_1_ID);
      const trend2 = computeTrendData(COMPANY_2_ID);
      setTrendData(trend1); // Default to company 1 trend in comparison
    } else {
      const companyId = parseInt(selectedCompanyId);
      const pl = computeCompanyPL(companyId);
      setPlData(pl);
      setComparisonData(null);

      const trend = computeTrendData(companyId);
      setTrendData(trend);

      // Expense breakdown
      const allCats = { ...pl.expenseCategories, ...pl.sharedCategories };
      const breakdown = Object.entries(allCats)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
      setExpenseBreakdown(breakdown);

      // Shared expense detail
      const detail = sharedExpenses.map(exp => ({
        name: exp.name,
        vendor: exp.vendor,
        total: parseFloat(exp.price || 0),
        yourShare: parseFloat(exp.price || 0) / 2,
        date: exp.expenseDate,
        sourceOfFunds: exp.sourceOfFunds
      }));
      setSharedExpenseDetail(detail);
    }
  };

  // =============================================
  // FORMATTING UTILITIES
  // =============================================
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);
  };

  const formatCurrencyFull = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount || 0);
  };

  const formatCurrencyShort = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value?.toFixed(0);
  };

  const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-gray-900 mb-1">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }} className="flex justify-between gap-4">
            <span>{entry.name}:</span>
            <span className="font-medium">{formatCurrency(entry.value)}</span>
          </p>
        ))}
      </div>
    );
  };

  const setQuickRange = (range) => {
    const now = new Date();
    let start;
    switch (range) {
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        now.setDate(0);
        break;
      case 'thisQuarter':
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'last6Months':
        start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case 'lastYear':
        start = new Date(now.getFullYear() - 1, 0, 1);
        now.setFullYear(now.getFullYear() - 1, 11, 31);
        break;
      default:
        return;
    }
    setDateRange({
      startDate: start.toISOString().split('T')[0],
      endDate: range === 'lastMonth' || range === 'lastYear' ? now.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
  };

  // =============================================
  // EXPORT FUNCTIONS
  // =============================================
  const handleExportCSV = () => {
    if (!plData) return;

    let csvContent = '';

    if (selectedView === 'comparison' && comparisonData) {
      const { company1: c1, company2: c2, combined } = comparisonData;
      csvContent = [
        `Profit & Loss Report - Comparison`,
        `Period: ${dateRange.startDate} to ${dateRange.endDate}`,
        '',
        `Item,${c1.companyName},${c2.companyName},Combined`,
        `Revenue,${c1.revenue.toFixed(2)},${c2.revenue.toFixed(2)},${combined.revenue.toFixed(2)}`,
        `Cost of Goods Sold,${c1.cogs.toFixed(2)},${c2.cogs.toFixed(2)},${combined.cogs.toFixed(2)}`,
        `Gross Profit,${c1.grossProfit.toFixed(2)},${c2.grossProfit.toFixed(2)},${combined.grossProfit.toFixed(2)}`,
        `Gross Margin %,${c1.grossMargin.toFixed(1)}%,${c2.grossMargin.toFixed(1)}%,${combined.grossMargin.toFixed(1)}%`,
        `Own Expenses,${c1.ownExpenses.toFixed(2)},${c2.ownExpenses.toFixed(2)},${(c1.ownExpenses + c2.ownExpenses).toFixed(2)}`,
        `Shared Expenses (50%),${c1.sharedExpenses.toFixed(2)},${c2.sharedExpenses.toFixed(2)},${(c1.sharedExpenses + c2.sharedExpenses).toFixed(2)}`,
        `Total Expenses,${c1.totalExpenses.toFixed(2)},${c2.totalExpenses.toFixed(2)},${combined.totalExpenses.toFixed(2)}`,
        `Net Profit,${c1.netProfit.toFixed(2)},${c2.netProfit.toFixed(2)},${combined.netProfit.toFixed(2)}`,
        `Net Margin %,${c1.netMargin.toFixed(1)}%,${c2.netMargin.toFixed(1)}%,${combined.netMargin.toFixed(1)}%`,
        '',
        'Monthly Trend',
        'Period,Revenue,COGS,Gross Profit,Own Expenses,Shared Expenses,Net Profit,Margin %'
      ].join('\n');

      trendData.forEach(row => {
        csvContent += `\n${row.label},${row.revenue.toFixed(2)},${row.cogs.toFixed(2)},${row.grossProfit.toFixed(2)},${row.ownExpenses.toFixed(2)},${row.sharedExpenses.toFixed(2)},${row.netProfit.toFixed(2)},${row.margin.toFixed(1)}%`;
      });
    } else {
      csvContent = [
        `Profit & Loss Report - ${selectedCompanyName}`,
        `Period: ${dateRange.startDate} to ${dateRange.endDate}`,
        '',
        'PROFIT & LOSS STATEMENT',
        '',
        `Revenue (Sales),${plData.revenue.toFixed(2)}`,
        `Cost of Goods Sold,${plData.cogs.toFixed(2)}`,
        `Gross Profit,${plData.grossProfit.toFixed(2)}`,
        `Gross Margin,${plData.grossMargin.toFixed(1)}%`,
        '',
        'OPERATING EXPENSES',
        `Own Expenses,${plData.ownExpenses.toFixed(2)}`,
        `Shared Expenses (50% of Vatu-Amazing),${plData.sharedExpenses.toFixed(2)}`,
        `Total Operating Expenses,${plData.totalExpenses.toFixed(2)}`,
        '',
        `NET PROFIT / (LOSS),${plData.netProfit.toFixed(2)}`,
        `Net Profit Margin,${plData.netMargin.toFixed(1)}%`,
        '',
        'EXPENSE BREAKDOWN',
        'Category,Amount'
      ].join('\n');

      expenseBreakdown.forEach(item => {
        csvContent += `\n"${item.name}",${item.value.toFixed(2)}`;
      });

      csvContent += '\n\nMONTHLY TREND\nPeriod,Revenue,COGS,Gross Profit,Own Expenses,Shared Expenses,Net Profit,Margin %';
      trendData.forEach(row => {
        csvContent += `\n${row.label},${row.revenue.toFixed(2)},${row.cogs.toFixed(2)},${row.grossProfit.toFixed(2)},${row.ownExpenses.toFixed(2)},${row.sharedExpenses.toFixed(2)},${row.netProfit.toFixed(2)},${row.margin.toFixed(1)}%`;
      });

      if (sharedExpenseDetail.length > 0) {
        csvContent += '\n\nSHARED EXPENSES DETAIL (Vatu-Amazing)\nExpense,Vendor,Total,Your 50% Share,Date';
        sharedExpenseDetail.forEach(item => {
          csvContent += `\n"${item.name}","${item.vendor}",${item.total.toFixed(2)},${item.yourShare.toFixed(2)},${item.date}`;
        });
      }
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profit_loss_${selectedView === 'comparison' ? 'comparison' : selectedCompanyName}_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  // =============================================
  // RENDER HELPERS
  // =============================================
  const SectionHeader = ({ title, section, icon: Icon }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors rounded-t-lg"
    >
      <div className="flex items-center space-x-2">
        <Icon className="w-5 h-5 text-indigo-600" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      {expandedSections[section] ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
    </button>
  );

  const PLRow = ({ label, amount, bold = false, indent = false, negative = false, highlight = false, border = false }) => (
    <tr className={`${bold ? 'font-bold' : ''} ${highlight ? 'bg-indigo-50' : ''} ${border ? 'border-t-2 border-gray-800' : ''}`}>
      <td className={`py-2 ${indent ? 'pl-8' : 'pl-4'} text-sm ${bold ? 'text-gray-900 font-semibold' : 'text-gray-700'}`}>
        {label}
      </td>
      <td className={`py-2 pr-4 text-right text-sm ${
        bold ? 'font-semibold' : ''
      } ${
        amount < 0 ? 'text-red-600' : amount > 0 && highlight ? 'text-green-700' : 'text-gray-900'
      }`}>
        {negative && amount > 0 ? `(${formatCurrencyFull(amount)})` : formatCurrencyFull(amount)}
      </td>
    </tr>
  );

  const ProfitIndicator = ({ value }) => {
    if (value > 0) return <ArrowUpRight className="w-4 h-4 text-green-500" />;
    if (value < 0) return <ArrowDownRight className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  // Filter business companies (exclude shared company from selector)
  const businessCompanies = useMemo(() => companies.filter(c => c.id !== SHARED_COMPANY_ID), [companies]);

  // =============================================
  // LOADING STATE
  // =============================================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!plData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-gray-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>Select a company to view Profit & Loss report</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profit & Loss Report</h1>
          <p className="text-gray-600">Comprehensive income statement with shared expense analysis</p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-2">
          <button onClick={handleExportCSV} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
          <button onClick={handlePrint} className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2">
            <Printer className="w-4 h-4" />
            <span>Print</span>
          </button>
          <button onClick={loadAllData} className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
          {/* View Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">View</label>
            <select
              value={selectedView}
              onChange={(e) => setSelectedView(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="single">Single Company</option>
              <option value="comparison">Company Comparison</option>
            </select>
          </div>

          {/* Company Selector */}
          {selectedView === 'single' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
              <div className="flex items-center space-x-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                <select
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {businessCompanies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Group By</label>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          {/* Quick Range */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Quick Range</label>
            <div className="flex flex-wrap gap-1">
              {[
                { key: 'thisMonth', label: 'This Month' },
                { key: 'lastMonth', label: 'Last Month' },
                { key: 'thisQuarter', label: 'This Qtr' },
                { key: 'thisYear', label: 'This Year' },
                { key: 'last6Months', label: '6 Months' },
                { key: 'lastYear', label: 'Last Year' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setQuickRange(key)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block text-center mb-6">
        <h2 className="text-xl font-bold">{selectedView === 'comparison' ? 'Company Comparison' : selectedCompanyName}</h2>
        <h3 className="text-lg">Profit & Loss Statement</h3>
        <p className="text-sm text-gray-600">Period: {dateRange.startDate} to {dateRange.endDate}</p>
      </div>

      {/* ===== COMPARISON VIEW ===== */}
      {selectedView === 'comparison' && comparisonData && (
        <>
          {/* Comparison Summary Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[comparisonData.company1, comparisonData.company2, comparisonData.combined].map((data, idx) => (
              <div key={idx} className={`rounded-lg shadow-sm border p-5 ${idx === 2 ? 'bg-indigo-50 border-indigo-200' : 'bg-white'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${idx === 2 ? 'text-indigo-700' : 'text-gray-700'}`}>
                  {data.companyName}
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Revenue</span>
                    <span className="text-sm font-medium">{formatCurrency(data.revenue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">COGS</span>
                    <span className="text-sm text-red-600">({formatCurrency(data.cogs)})</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="text-sm font-medium text-gray-700">Gross Profit</span>
                    <span className="text-sm font-medium">{formatCurrency(data.grossProfit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Expenses</span>
                    <span className="text-sm text-red-600">({formatCurrency(data.totalExpenses)})</span>
                  </div>
                  <div className="flex justify-between border-t-2 border-gray-800 pt-1">
                    <span className="text-sm font-bold">Net Profit</span>
                    <span className={`text-sm font-bold ${data.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(data.netProfit)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500">Margin</span>
                    <span className={`text-xs font-medium ${data.netMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercent(data.netMargin)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Comparison Bar Chart */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Company Comparison</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={[comparisonData.company1, comparisonData.company2].map(c => ({
                  name: c.companyName,
                  Revenue: c.revenue,
                  COGS: c.cogs,
                  'Gross Profit': c.grossProfit,
                  'Own Expenses': c.ownExpenses,
                  'Shared (50%)': c.sharedExpenses,
                  'Net Profit': c.netProfit
                }))}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="Revenue" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Gross Profit" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Own Expenses" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Shared (50%)" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Net Profit" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Shared Expense Breakdown */}
          <div className="bg-white rounded-lg shadow-sm border">
            <SectionHeader title="Shared Expenses (Vatu-Amazing)" section="shared" icon={ArrowLeftRight} />
            {expandedSections.shared && (
              <div className="p-4 pt-0">
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-cyan-800">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    These expenses are captured under <strong>Vatu-Amazing (Company {SHARED_COMPANY_ID})</strong> and split 50/50 between both companies.
                    Total shared: <strong>{formatCurrency(sharedExpenses.reduce((s, e) => s + parseFloat(e.price || 0), 0))}</strong> →
                    Each company bears: <strong>{formatCurrency(sharedExpenses.reduce((s, e) => s + parseFloat(e.price || 0), 0) / 2)}</strong>
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expense</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-blue-600 uppercase">Per Company (50%)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sharedExpenseDetail.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{item.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{item.vendor}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{item.date}</td>
                          <td className="px-4 py-2 text-sm text-right text-gray-900">{formatCurrencyFull(item.total)}</td>
                          <td className="px-4 py-2 text-sm text-right font-medium text-blue-700">{formatCurrencyFull(item.yourShare)}</td>
                        </tr>
                      ))}
                      {sharedExpenseDetail.length > 0 && (
                        <tr className="bg-gray-50 font-bold">
                          <td colSpan={3} className="px-4 py-2 text-sm text-gray-900">TOTAL</td>
                          <td className="px-4 py-2 text-sm text-right text-gray-900">
                            {formatCurrencyFull(sharedExpenseDetail.reduce((s, i) => s + i.total, 0))}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-blue-700">
                            {formatCurrencyFull(sharedExpenseDetail.reduce((s, i) => s + i.yourShare, 0))}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ===== SINGLE COMPANY VIEW ===== */}
      {selectedView === 'single' && plData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Revenue</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(plData.revenue)}</p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{plData.invoiceCount} invoices</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">COGS</p>
                  <p className="text-xl font-bold text-red-600 mt-1">{formatCurrency(plData.cogs)}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Cost of goods sold</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Gross Profit</p>
                  <p className={`text-xl font-bold mt-1 ${plData.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(plData.grossProfit)}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Margin: {formatPercent(plData.grossMargin)}</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Own Expenses</p>
                  <p className="text-xl font-bold text-amber-600 mt-1">{formatCurrency(plData.ownExpenses)}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-full">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Direct expenses</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Shared (50%)</p>
                  <p className="text-xl font-bold text-cyan-600 mt-1">{formatCurrency(plData.sharedExpenses)}</p>
                </div>
                <div className="p-3 bg-cyan-100 rounded-full">
                  <ArrowLeftRight className="w-6 h-6 text-cyan-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Vatu-Amazing split</p>
            </div>

            <div className={`rounded-lg shadow-sm border p-5 ${plData.netProfit >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Net Profit</p>
                  <p className={`text-xl font-bold mt-1 ${plData.netProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(plData.netProfit)}
                  </p>
                </div>
                <ProfitIndicator value={plData.netProfit} />
              </div>
              <p className="text-xs text-gray-500 mt-2">Margin: {formatPercent(plData.netMargin)}</p>
            </div>
          </div>

          {/* P&L Statement Table */}
          <div className="bg-white rounded-lg shadow-sm border">
            <SectionHeader title="Profit & Loss Statement" section="statement" icon={FileSpreadsheet} />
            {expandedSections.statement && (
              <div className="p-4 pt-0">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-800">
                      <th className="py-2 pl-4 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="py-2 pr-4 text-right text-sm font-semibold text-gray-900">Amount (KES)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <PLRow label="Revenue (Sales)" amount={plData.revenue} bold />
                    <PLRow label="Less: Cost of Goods Sold" amount={plData.cogs} indent negative />
                    <PLRow label="GROSS PROFIT" amount={plData.grossProfit} bold highlight border />
                    <tr><td colSpan={2} className="py-2"></td></tr>
                    <PLRow label="Operating Expenses:" amount={null} bold />
                    <PLRow label={`Direct Expenses (${selectedCompanyName})`} amount={plData.ownExpenses} indent negative />
                    <PLRow label={`Shared Expenses (50% of Vatu-Amazing)`} amount={plData.sharedExpenses} indent negative />
                    <PLRow label="TOTAL OPERATING EXPENSES" amount={plData.totalExpenses} bold border negative />
                    <tr><td colSpan={2} className="py-2"></td></tr>
                    <PLRow label="NET PROFIT / (LOSS)" amount={plData.netProfit} bold highlight border />
                  </tbody>
                  <tfoot>
                    <tr className="border-t">
                      <td className="py-2 pl-4 text-xs text-gray-500">Gross Margin: {formatPercent(plData.grossMargin)}</td>
                      <td className="py-2 pr-4 text-right text-xs text-gray-500">Net Margin: {formatPercent(plData.netMargin)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Trend Charts */}
          <div className="bg-white rounded-lg shadow-sm border">
            <SectionHeader title="Profit & Loss Trend" section="trends" icon={TrendingUp} />
            {expandedSections.trends && (
              <div className="p-4 pt-0 space-y-6">
                {/* Revenue vs Expenses vs Profit */}
                {trendData.length > 0 ? (
                  <>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Revenue vs Expenses vs Net Profit</h4>
                      <ResponsiveContainer width="100%" height={350}>
                        <ComposedChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <defs>
                            <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.2} />
                              <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 11 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#4f46e5" fill="url(#revGrad)" strokeWidth={2} />
                          <Bar dataKey="totalExpenses" name="Total Expenses" fill="#f59e0b" radius={[4, 4, 0, 0]} opacity={0.7} />
                          <Line type="monotone" dataKey="netProfit" name="Net Profit" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Profit Margin Trend */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Profit Margin Trend (%)</h4>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <defs>
                            <linearGradient id="marginGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={(v) => `${v.toFixed(0)}%`} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Margin']} />
                          <Area type="monotone" dataKey="margin" name="Net Margin" stroke="#8b5cf6" fill="url(#marginGrad)" strokeWidth={2} />
                          <Line type="monotone" dataKey="margin" stroke="#8b5cf6" strokeWidth={0} dot={{ r: 3, fill: '#8b5cf6' }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Period Detail Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">COGS</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Gross Profit</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-amber-600 uppercase">Own Exp.</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-cyan-600 uppercase">Shared</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Net Profit</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Margin</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {trendData.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium text-gray-900">{row.label}</td>
                              <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(row.revenue)}</td>
                              <td className="px-3 py-2 text-right text-red-600">({formatCurrency(row.cogs)})</td>
                              <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(row.grossProfit)}</td>
                              <td className="px-3 py-2 text-right text-amber-600">({formatCurrency(row.ownExpenses)})</td>
                              <td className="px-3 py-2 text-right text-cyan-600">({formatCurrency(row.sharedExpenses)})</td>
                              <td className={`px-3 py-2 text-right font-bold ${row.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(row.netProfit)}
                              </td>
                              <td className={`px-3 py-2 text-right ${row.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatPercent(row.margin)}
                              </td>
                            </tr>
                          ))}
                          {/* Totals row */}
                          {trendData.length > 0 && (
                            <tr className="bg-gray-100 font-bold">
                              <td className="px-3 py-2 text-gray-900">TOTAL</td>
                              <td className="px-3 py-2 text-right">{formatCurrency(plData.revenue)}</td>
                              <td className="px-3 py-2 text-right text-red-600">({formatCurrency(plData.cogs)})</td>
                              <td className="px-3 py-2 text-right">{formatCurrency(plData.grossProfit)}</td>
                              <td className="px-3 py-2 text-right text-amber-600">({formatCurrency(plData.ownExpenses)})</td>
                              <td className="px-3 py-2 text-right text-cyan-600">({formatCurrency(plData.sharedExpenses)})</td>
                              <td className={`px-3 py-2 text-right ${plData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(plData.netProfit)}
                              </td>
                              <td className={`px-3 py-2 text-right ${plData.netMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatPercent(plData.netMargin)}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">No trend data for the selected period</div>
                )}
              </div>
            )}
          </div>

          {/* Expense Breakdown */}
          <div className="bg-white rounded-lg shadow-sm border">
            <SectionHeader title="Expense Breakdown" section="expenses" icon={PieChartIcon} />
            {expandedSections.expenses && (
              <div className="p-4 pt-0">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pie Chart */}
                  <div>
                    {expenseBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={expenseBreakdown}
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                            dataKey="value"
                            label={({ name, percent }) => `${name.length > 15 ? name.substring(0, 15) + '...' : name} (${(percent * 100).toFixed(0)}%)`}
                            labelLine={{ strokeWidth: 1 }}
                          >
                            {expenseBreakdown.map((_, idx) => (
                              <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrencyFull(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-12 text-gray-500">No expenses in this period</div>
                    )}
                  </div>
                  {/* Expense List */}
                  <div className="overflow-y-auto max-h-[350px]">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expense</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">% of Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {expenseBreakdown.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-900 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                              <span className={item.name.includes('Shared') ? 'text-cyan-700 font-medium' : ''}>{item.name}</span>
                            </td>
                            <td className="px-3 py-2 text-right text-gray-900">{formatCurrencyFull(item.value)}</td>
                            <td className="px-3 py-2 text-right text-gray-600">
                              {plData.totalExpenses > 0 ? (item.value / plData.totalExpenses * 100).toFixed(1) : 0}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Shared Expense Detail */}
          {sharedExpenseDetail.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border">
              <SectionHeader title="Shared Expenses Detail (Vatu-Amazing)" section="shared" icon={ArrowLeftRight} />
              {expandedSections.shared && (
                <div className="p-4 pt-0">
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-cyan-800">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      Expenses captured under <strong>Vatu-Amazing</strong> are split 50/50. Your share: <strong>{formatCurrency(plData.sharedExpenses)}</strong>
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expense</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Full Amount</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-blue-600 uppercase">Your 50%</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {sharedExpenseDetail.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-900 font-medium">{item.name}</td>
                            <td className="px-4 py-2 text-gray-600">{item.vendor}</td>
                            <td className="px-4 py-2 text-gray-600">{item.sourceOfFunds || '-'}</td>
                            <td className="px-4 py-2 text-gray-600">{item.date}</td>
                            <td className="px-4 py-2 text-right text-gray-900">{formatCurrencyFull(item.total)}</td>
                            <td className="px-4 py-2 text-right font-bold text-blue-700">{formatCurrencyFull(item.yourShare)}</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-bold">
                          <td colSpan={4} className="px-4 py-2 text-gray-900">TOTAL</td>
                          <td className="px-4 py-2 text-right text-gray-900">
                            {formatCurrencyFull(sharedExpenseDetail.reduce((s, i) => s + i.total, 0))}
                          </td>
                          <td className="px-4 py-2 text-right text-blue-700">
                            {formatCurrencyFull(sharedExpenseDetail.reduce((s, i) => s + i.yourShare, 0))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProfitLossReport;